/**
 * Work Board (#343 / #430) — one canonical Kanban projection.
 *
 * Cards are derived from existing Brunel reads in lib/work-board.ts. #430 keeps
 * those cards compact and opens recorded details in a modal; there is still no
 * second task store or drag/drop path. #671 adds one bounded owner action in the
 * detail of an exact canonical task: Discard, through the existing guarded
 * `discard_task` RPC. Cards still move only when Brunel state changes.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type {
  Approval,
  CommandPlanRow,
  FinishedRun,
  MissionTask,
  NodeCapacityRow,
  Plan,
  Project,
  RoadmapItemRow,
  RunningTask,
  TaskActionRow,
  WaitingJobRecoveryRow,
  Worker,
  WorkerTerminalRow,
} from '../lib/data';
import {
  buildWorkBoard,
  workBoardField,
  type WorkBoardCard,
  type WorkBoardColumnId,
} from '../lib/work-board';
import { taskActionFor, type DiscardTaskResult } from '../lib/discard-task';
import {
  buildHistoryCards,
  DEFAULT_WORK_BOARD_PAGE_SIZE,
  historyRangeLabel,
  WORK_BOARD_PAGE_SIZES,
  workBoardPageSize,
  type WorkBoardHistoryResult,
  type WorkBoardPageSize,
} from '../lib/work-board-history';
import '../work-board.css';
import type { ControlReceiptView } from '../lib/control-receipt';
import { ControlReceiptNote } from './ControlReceiptNote';

export interface WorkBoardProps {
  readonly orgId: string;
  readonly roadmap: readonly RoadmapItemRow[];
  readonly plans: readonly Plan[];
  readonly missionTasks: readonly MissionTask[];
  readonly running: readonly RunningTask[];
  readonly results: readonly FinishedRun[];
  readonly commandPlans: readonly CommandPlanRow[];
  readonly workerTerminals: readonly WorkerTerminalRow[];
  readonly approvals: readonly Approval[];
  readonly workers: readonly Worker[];
  readonly projects: readonly Project[];
  readonly capacity: readonly NodeCapacityRow[];
  readonly nowMs: number;
  /** #671: canonical `brunel.task_actions` rows; the board never works out an action itself. */
  readonly taskActions?: readonly TaskActionRow[];
  /** Jobs Brunel closed without running them (`brunel.waiting_job_recoveries`), for the card's one line. */
  readonly jobRecoveries?: readonly WaitingJobRecoveryRow[];
  /** #671: the owner's discard request for one exact task; absent means no action is shown. */
  readonly onDiscardTask?: (action: TaskActionRow, onResult: (result: DiscardTaskResult) => void) => Promise<void>;
  readonly onRefresh?: () => Promise<void>;
  readonly lastRefreshedAt?: number | null;
  readonly onDraftWork?: (card: WorkBoardCard) => void;
  readonly onOpenRoadmapItem?: (itemId: string) => void;
  readonly onReviewPlans?: () => void;
  readonly onResolveApprovals?: () => void;
  readonly onOpenWorker?: (workerId: string) => void;
  readonly onStopRun?: (assignmentId: string) => Promise<void>;
  /** Receipt for the Stop run press in this session. Success stays at Sent. */
  readonly stopReceipt?: ControlReceiptView | null;
  readonly onRetryTask?: (action: TaskActionRow, onResult: (result: DiscardTaskResult) => void) => Promise<void>;
  /** Optional observer hook only; the canonical card click always opens the detail modal. */
  readonly onSelectCard?: (card: WorkBoardCard) => void;
  /**
   * #676: one server page of finished work. When present, History pages the canonical history
   * index instead of showing only what the Current reads happen to hold.
   */
  readonly loadHistoryPage?: (page: number, pageSize: WorkBoardPageSize) => Promise<WorkBoardHistoryResult>;
}

export type WorkBoardMode = 'current' | 'history';

function sourceRecordId(card: WorkBoardCard): string | null {
  const prefix = `${card.sourceKind}:`;
  return card.id.startsWith(prefix) ? card.id.slice(prefix.length) || null : card.refId;
}

function refreshedLabel(value: number | null | undefined): string {
  if (value == null) return 'Refresh time unknown';
  return `Updated ${new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' }).format(value)}`;
}

function Meta({ label, value, important = false }: {
  label: string;
  value: string | null;
  important?: boolean;
}): ReactElement {
  return (
    <span className={important ? 'work-board-field important' : 'work-board-field'} title={`${label}: ${workBoardField(value)}`}>
      <span className="work-board-field-label">{label}</span>
      <span className="work-board-field-value">{workBoardField(value)}</span>
    </span>
  );
}

function CardView({
  card,
  onSelect,
}: {
  card: WorkBoardCard;
  onSelect: (card: WorkBoardCard) => void;
}): ReactElement {
  return (
    <button
      type="button"
      className="work-board-card"
      data-source={card.sourceKind}
      data-column={card.column}
      onClick={() => onSelect(card)}
      aria-label={`${card.title}, ${card.currentState}. Open recorded details`}
    >
      <span className="work-board-card-head">
        <span className="work-board-card-title">{card.title}</span>
        <span className="work-board-state" data-state={card.column}>{card.currentState}</span>
      </span>
      <span className="work-board-card-meta">
        <Meta label="Project" value={card.projectName} important />
        <Meta label="Worker" value={card.workerLabel} important />
        <Meta label="Runtime" value={card.runtimeModel} important />
        <Meta label="Host" value={card.executionHost} />
      </span>
      {(card.blockerOrApproval !== null || card.testOrReview !== null) && (
        <span className="work-board-card-flags">
          {card.blockerOrApproval !== null && <span className="work-board-flag blocker">{card.blockerOrApproval}</span>}
          {card.testOrReview !== null && <span className="work-board-flag review">{card.testOrReview}</span>}
        </span>
      )}
    </button>
  );
}

function ColumnView({
  id,
  label,
  answers,
  cards,
  onSelect,
}: {
  id: WorkBoardColumnId;
  label: string;
  answers: string;
  cards: readonly WorkBoardCard[];
  onSelect: (card: WorkBoardCard) => void;
}): ReactElement {
  return (
    <section className="work-board-column" data-column={id} aria-label={label}>
      <header className="work-board-column-head">
        <span className="work-board-column-title">
          <h3>{label}</h3>
          <span className="work-board-column-count" aria-label={`${cards.length} cards`}>{cards.length}</span>
        </span>
        <p className="work-board-column-answers muted">{answers}</p>
      </header>
      <div className="work-board-column-body">
        {cards.length === 0 ? (
          <p className="work-board-empty muted">Nothing here</p>
        ) : (
          cards.map((card) => <CardView key={card.id} card={card} onSelect={onSelect} />)
        )}
      </div>
    </section>
  );
}

export function workBoardModalKeyShouldClose(key: string): boolean {
  return key === 'Escape';
}

function DetailField({ label, value }: { label: string; value: string | null | undefined }): ReactElement {
  return (
    <div className="work-board-detail-field">
      <dt>{label}</dt>
      <dd>{workBoardField(value)}</dd>
    </div>
  );
}

/**
 * #671: the one owner action in a task's detail. Shown only for an exact canonical task row;
 * `can_discard` and the note come from `brunel.task_actions`, and the result shown is the
 * server's answer or the re-read canonical state, never a local guess.
 */
export function TaskDiscardAction({
  action,
  onDiscard,
}: {
  readonly action: TaskActionRow;
  readonly onDiscard: (action: TaskActionRow, onResult: (result: DiscardTaskResult) => void) => Promise<void>;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DiscardTaskResult | null>(null);
  const discard = () => {
    setBusy(true);
    setResult(null);
    void onDiscard(action, setResult).finally(() => setBusy(false));
  };
  return (
    <section className="work-board-detail-action" aria-label="Task action" data-testid="work-board-task-action">
      <button
        type="button"
        className="ghost"
        disabled={!action.can_discard || busy}
        onClick={discard}
        title={action.can_discard ? 'Put this task aside. Its runs and history are kept.' : undefined}
      >
        {busy ? 'Discarding…' : 'Discard task'}
      </button>
      {!action.can_discard && (
        <p className="muted work-board-detail-note">
          Brunel keeps this task: {action.note ?? 'it cannot be discarded now'}.
        </p>
      )}
      {result !== null && (
        <p className={result.ok ? 'muted work-board-detail-note' : 'error'} role={result.ok ? 'status' : 'alert'}>
          {result.message}
        </p>
      )}
    </section>
  );
}

export function WorkBoardDetailModal({
  card,
  onClose,
  taskAction = null,
  onDiscardTask,
  onDraftWork,
  onOpenRoadmapItem,
  onReviewPlans,
  onResolveApprovals,
  onOpenWorker,
  onStopRun,
  onRetryTask,
  stopReceipt = null,
}: {
  readonly card: WorkBoardCard;
  readonly onClose: () => void;
  readonly taskAction?: TaskActionRow | null;
  readonly onDiscardTask?: (action: TaskActionRow, onResult: (result: DiscardTaskResult) => void) => Promise<void>;
  readonly onDraftWork?: (card: WorkBoardCard) => void;
  readonly onOpenRoadmapItem?: (itemId: string) => void;
  readonly onReviewPlans?: () => void;
  readonly onResolveApprovals?: () => void;
  readonly onOpenWorker?: (workerId: string) => void;
  readonly onStopRun?: (assignmentId: string) => Promise<void>;
  readonly onRetryTask?: (action: TaskActionRow, onResult: (result: DiscardTaskResult) => void) => Promise<void>;
  readonly stopReceipt?: ControlReceiptView | null;
}): ReactElement {
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionResult, setActionResult] = useState<DiscardTaskResult | null>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (!workBoardModalKeyShouldClose(event.key)) return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [onClose]);

  const titleId = `work-board-detail-${card.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const sourceId = sourceRecordId(card);
  const immutableHistory = card.sourceKind === 'finished'
    || (card.sourceKind === 'task' && (card.currentState === 'completed' || card.currentState === 'discarded'))
    || (card.sourceKind === 'roadmap' && card.currentState.startsWith('done'))
    || (card.sourceKind === 'command_plan' && (card.currentState === 'completed' || card.currentState === 'withdrawn'));
  const runAction = (action: () => Promise<void>) => {
    setActionBusy(true);
    setActionResult(null);
    void action().finally(() => setActionBusy(false));
  };
  const retry = () => {
    if (taskAction === null || onRetryTask === undefined) return;
    setActionBusy(true);
    setActionResult(null);
    void onRetryTask(taskAction, setActionResult).finally(() => setActionBusy(false));
  };

  return (
    <div
      className="work-board-modal-backdrop"
      role="presentation"
      data-testid="work-board-detail-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="work-board-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="work-board-detail-modal"
      >
        <header className="work-board-detail-head">
          <div className="stack tight">
            <span className="work-board-detail-source">{card.sourceKind.replace('_', ' ')}</span>
            <h2 id={titleId}>{card.title}</h2>
          </div>
          <button ref={closeButton} type="button" className="ghost icon" aria-label="Close work details" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="work-board-detail-body">
          <section className="work-board-detail-summary">
            <span className="work-board-state" data-state={card.column}>{card.currentState}</span>
            {card.blockerOrApproval !== null && <span className="work-board-flag blocker">{card.blockerOrApproval}</span>}
            {card.testOrReview !== null && <span className="work-board-flag review">{card.testOrReview}</span>}
          </section>

          <dl className="work-board-detail-grid">
            <DetailField label="Source kind" value={card.sourceKind} />
            <DetailField label="Project" value={card.projectName} />
            <DetailField label="Worker" value={card.workerLabel} />
            <DetailField label="Runtime / model" value={card.runtimeModel} />
            <DetailField label="Node / computer" value={card.executionHost} />
            <DetailField label="Current state" value={card.currentState} />
            <DetailField label="Blocker / approval" value={card.blockerOrApproval} />
            <DetailField label="Elapsed / timing" value={card.elapsed} />
            <DetailField label="Tests / review / result" value={card.testOrReview} />
            <DetailField label="Capacity warning" value={card.usageWarning} />
            <DetailField label="Task / canonical reference" value={card.taskId ?? card.refId} />
            <DetailField label="Source record" value={card.id} />
            <DetailField label="Related reference" value={card.refId} />
            <DetailField label="Branch" value={card.branch} />
            <DetailField label="Pull request" value={card.pullRequest} />
          </dl>
          <section className="work-board-detail-actions" aria-label="Available actions">
            <h3>Available actions</h3>
            <div className="row wrap">
              {card.sourceKind === 'roadmap' && sourceId !== null && onOpenRoadmapItem !== undefined && (
                <button type="button" onClick={() => onOpenRoadmapItem(sourceId)}>Open in Roadmap</button>
              )}
              {!immutableHistory && (card.sourceKind === 'roadmap' || card.sourceKind === 'task') && onDraftWork !== undefined && (
                <button type="button" className="primary" onClick={() => onDraftWork(card)}>
                  {card.projectName === null ? 'Plan this work' : 'Work on this project'}
                </button>
              )}
              {card.sourceKind === 'plan' && card.currentState === 'proposed' && onReviewPlans !== undefined && (
                <button type="button" className="primary" onClick={onReviewPlans}>Review, approve or reject plan</button>
              )}
              {card.sourceKind === 'approval' && onResolveApprovals !== undefined && (
                <button type="button" className="primary" onClick={onResolveApprovals}>Resolve approval</button>
              )}
              {(card.sourceKind === 'terminal' || card.sourceKind === 'running' || card.sourceKind === 'command_plan')
                && card.workerId !== null && onOpenWorker !== undefined && (
                <button type="button" onClick={() => onOpenWorker(card.workerId!)}>Open Worker</button>
              )}
              {card.sourceKind === 'running' && sourceId !== null && onStopRun !== undefined && (
                <button type="button" className="danger" disabled={actionBusy}
                  onClick={() => runAction(() => onStopRun(sourceId))}>
                  {actionBusy ? 'Stopping…' : 'Stop run'}
                </button>
              )}
              {stopReceipt !== null && <ControlReceiptNote view={stopReceipt} action="Stop run" />}
              {card.sourceKind === 'task' && card.currentState === 'failed'
                && taskAction !== null && onRetryTask !== undefined && (
                <button type="button" className="primary" disabled={actionBusy} onClick={retry}>
                  {actionBusy ? 'Retrying…' : 'Retry task'}
                </button>
              )}
            </div>
            {immutableHistory && (
              <p className="muted work-board-action-reason">
                This is finished history. Its evidence is preserved; start follow-up work through Main Command instead of changing the old record.
              </p>
            )}
            {card.sourceKind === 'command_plan' && card.workerId === null && (
              <p className="muted work-board-action-reason">
                This command plan has no exact Worker to open. Resolve it from Main Command, where its plan authority and routing are visible.
              </p>
            )}
            {actionResult !== null && (
              <p className={actionResult.ok ? 'muted work-board-action-reason' : 'error'} role={actionResult.ok ? 'status' : 'alert'}>
                {actionResult.message}
              </p>
            )}
          </section>
          {taskAction !== null && onDiscardTask !== undefined && (
            <TaskDiscardAction action={taskAction} onDiscard={onDiscardTask} />
          )}
          <p className="muted work-board-detail-note">
            Recorded Brunel data. Unknown means this Work Board does not have that fact; nothing is inferred.
            The card moves only when Brunel state changes.
          </p>
        </div>
      </section>
    </div>
  );
}

export function WorkBoard(props: WorkBoardProps): ReactElement {
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [mode, setMode] = useState<WorkBoardMode>('current');
  const [refreshing, setRefreshing] = useState(false);
  const model = useMemo(
    () => buildWorkBoard({
      orgId: props.orgId,
      roadmap: props.roadmap,
      plans: props.plans,
      missionTasks: props.missionTasks,
      running: props.running,
      results: props.results,
      commandPlans: props.commandPlans,
      workerTerminals: props.workerTerminals,
      approvals: props.approvals,
      workers: props.workers,
      projects: props.projects,
      capacity: props.capacity,
      nowMs: props.nowMs,
      ...(props.jobRecoveries === undefined ? {} : { jobRecoveries: props.jobRecoveries }),
    }),
    [
      props.orgId, props.roadmap, props.plans, props.missionTasks, props.running, props.results,
      props.commandPlans, props.workerTerminals, props.approvals, props.workers,
      props.projects, props.capacity, props.nowMs, props.jobRecoveries,
    ],
  );

  // #676: History reads one server page at a time; changing the page size returns to page 1.
  // The loader is read through a ref so a parent's inline callback does not re-read every render.
  const loadHistoryRef = useRef(props.loadHistoryPage);
  loadHistoryRef.current = props.loadHistoryPage;
  const paged = props.loadHistoryPage !== undefined;
  const [historyPage, setHistoryPage] = useState(1);
  const [historySize, setHistorySize] = useState<WorkBoardPageSize>(DEFAULT_WORK_BOARD_PAGE_SIZE);
  const [history, setHistory] = useState<WorkBoardHistoryResult | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  useEffect(() => {
    const load = loadHistoryRef.current;
    if (mode !== 'history' || load === undefined) return undefined;
    let live = true;
    setHistoryLoading(true);
    void load(historyPage, historySize)
      .catch((): WorkBoardHistoryResult => ({ ok: false, message: 'Could not load data.' }))
      .then((result) => { if (live) setHistory(result); })
      .finally(() => { if (live) setHistoryLoading(false); });
    return () => { live = false; };
  }, [mode, paged, historyPage, historySize, props.lastRefreshedAt]);
  const historyCards = useMemo(() => history?.ok === true
    ? buildHistoryCards({
      orgId: props.orgId, workers: props.workers, projects: props.projects, capacity: props.capacity, nowMs: props.nowMs,
    }, history)
    : [], [history, props.orgId, props.workers, props.projects, props.capacity, props.nowMs]);

  const visibleColumns = useMemo(() => {
    if (mode === 'history') {
      const done = model.columns.find((column) => column.id === 'done');
      if (done === undefined) return [];
      if (paged) {
        const answers = history?.ok === true
          ? historyRangeLabel(history.page, history.pageSize, historyCards.length, history.total)
          : 'Finished canonical records, one page at a time';
        return [{ ...done, label: 'HISTORY', answers, cards: historyCards }];
      }
      return [{ ...done, label: 'HISTORY', answers: 'Finished canonical records in the current bounded read' }];
    }
    return model.columns.map((column) => column.id === 'done'
      ? { ...column, cards: column.cards.slice(0, 20), answers: 'The 20 most recent finished records in this read' }
      : column);
  }, [mode, model, paged, history, historyCards]);
  const selectedCard = [...model.columns.flatMap((column) => column.cards), ...historyCards]
    .find((card) => card.id === selectedCardId) ?? null;
  const knownTaskActions = history?.ok === true
    ? [...history.taskActions, ...(props.taskActions ?? [])]
    : props.taskActions ?? [];

  const selectCard = (card: WorkBoardCard) => {
    setSelectedCardId(card.id);
    props.onSelectCard?.(card);
  };

  const refreshBoard = () => {
    if (props.onRefresh === undefined) return;
    setRefreshing(true);
    void props.onRefresh().finally(() => setRefreshing(false));
  };

  return (
    <div className="work-board" aria-label="Work Board">
      <header className="work-board-head">
        <div>
          <h2>Work Board</h2>
          <p className="muted">
            Canonical view of Roadmap, Planner, tasks, runs and reviews.
            Current is operational; History is preserved evidence, not a second task list.
          </p>
        </div>
        <div className="work-board-head-actions">
          <span className="work-board-total muted">{model.totalCards} cards</span>
          <span className="muted work-board-refreshed">{refreshedLabel(props.lastRefreshedAt)}</span>
          {props.onRefresh !== undefined && (
            <button type="button" className="ghost" disabled={refreshing} onClick={refreshBoard}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          )}
        </div>
      </header>
      <div className="work-board-modes" role="group" aria-label="Work Board view">
        <button type="button" className={mode === 'current' ? 'primary' : 'ghost'}
          aria-pressed={mode === 'current'} onClick={() => setMode('current')}>Current</button>
        <button type="button" className={mode === 'history' ? 'primary' : 'ghost'}
          aria-pressed={mode === 'history'} onClick={() => setMode('history')}>History</button>
      </div>
      {mode === 'history' && !paged && (
        <p className="muted work-board-history-note">
          This view shows the historical rows returned by Brunel&rsquo;s bounded readers. Refresh re-reads canonical state; it does not copy or delete history.
        </p>
      )}
      {mode === 'history' && paged && (
        <div className="work-board-history-pager" role="group" aria-label="History pages">
          <label>
            <span className="muted">Per page</span>{' '}
            <select value={historySize} onChange={(event) => {
              setHistorySize(workBoardPageSize(Number(event.target.value)));
              setHistoryPage(1);
            }}>
              {WORK_BOARD_PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
          <button type="button" className="ghost"
            disabled={historyLoading || history?.ok !== true || !history.hasPrevious}
            onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}>Previous</button>
          <span className="muted" aria-live="polite">
            {historyLoading ? 'Loading…'
              : history === null ? ''
              : history.ok ? historyRangeLabel(history.page, history.pageSize, historyCards.length, history.total)
              : history.message}
          </span>
          <button type="button" className="ghost"
            disabled={historyLoading || history?.ok !== true || !history.hasNext}
            onClick={() => setHistoryPage((page) => page + 1)}>Next</button>
        </div>
      )}
      <div className={`work-board-columns mode-${mode}`} role="list">
        {visibleColumns.map((col) => (
          <ColumnView
            key={col.id}
            id={col.id}
            label={col.label}
            answers={col.answers}
            cards={col.cards}
            onSelect={selectCard}
          />
        ))}
      </div>
      {selectedCard !== null && (
        <WorkBoardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCardId(null)}
          taskAction={taskActionFor(selectedCard, props.orgId, knownTaskActions)}
          onDiscardTask={props.onDiscardTask}
          onDraftWork={props.onDraftWork}
          onOpenRoadmapItem={props.onOpenRoadmapItem}
          onReviewPlans={props.onReviewPlans}
          onResolveApprovals={props.onResolveApprovals}
          onOpenWorker={props.onOpenWorker}
          onStopRun={props.onStopRun}
          stopReceipt={props.stopReceipt}
          onRetryTask={props.onRetryTask}
        />
      )}
    </div>
  );
}
